import { SECONDS_IN_DAY } from '../../helper/duration.constants';
import { IsoDate, IsoDateTime, IsoDateTimeTz, IsoDuration } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { addMillisecondsToDuration } from '../../helper/add-milliseconds-to-duration.function';
import { constructDate } from '../../helper/construct-date.function';

/**
 * Add a number of weeks to an ISO input while preserving its shape.
 *
 * Supported inputs:
 * - Date-only: YYYY-MM-DD -> returns YYYY-MM-DD
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])? -> returns same shape
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM) -> returns same shape, preserving the original tz suffix
 * - ISO 8601 Duration: P... -> returns a duration string
 *
 * Rules:
 * - amount must be a finite integer (can be negative). amount represents whole weeks.
 * - For date inputs, only the calendar date part changes; time-of-day and fractional seconds are preserved verbatim.
 */
export function addWeeks<T extends IsoDate | IsoDateTime | IsoDateTimeTz | IsoDuration>(input: T, amount: number): T {
  if(!Number.isInteger(amount) || !isFinite(amount))
    throw new Error('addWeeks: amount must be a finite integer');
  if(amount === 0)
    return input;

  const deconstructed = deconstructDate(input);
  if(deconstructed._type !== 'date'
    && deconstructed._type !== 'dateTime'
    && deconstructed._type !== 'dateTimeTz'
    && deconstructed._type !== 'duration') {
    throw new Error('addDays: invalid ISO date/time/duration string');
  }
  if(deconstructed._type === 'duration') {
    const returnValue = addMillisecondsToDuration(deconstructed, amount * 1000 * SECONDS_IN_DAY * 7);
    return constructDate(returnValue) as unknown as T;
  }
  const {year, month, day} = deconstructed;
  const dt = new Date(Date.UTC(year, month - 1, day));
  dt.setUTCDate(dt.getUTCDate() + amount * 7);
  deconstructed.year = dt.getUTCFullYear();
  deconstructed.month = dt.getUTCMonth() + 1;
  deconstructed.day = dt.getUTCDate();
  return constructDate(deconstructed) as unknown as T;
}
